import React, { MouseEvent } from 'react'
import {
  BodyText,
  IconButton,
  EmailIcon,
  ShareFacebookIcon,
  ShareMessengerIcon,
  ShareTwitterIcon,
  ShareWhatsappIcon,
  CopyToClipboardIcon,
  Row,
  Col,
  TitleText,
} from '@paypalcorp/pp-react'
import { Message } from '@paypalcorp/worldready-react'
import { addTrafficSourceParameter } from '../../../utils/links/shareLink'
import * as styles from './share.css'
import sharing from 'consumerweb-sharing'
import { useLocalizedContent } from '../../../hooks/useLocalizedContent'
import { FACEBOOK_APP_ID, LINK_SOURCE } from '../../../constants'
import {
  getGroupDetailPageVariant,
  tracking,
} from '../../../lib/analytics/fpti'
import Events from '../../../lib/analytics/eventTags.js'
import serverData from '../../../utils/server-data'
import { useAppDispatch } from '../../../store/hooks'
import { showGlobalToast } from '../../../store/slices/globalToastSlice'
import { isDetailsV1Dot1Enabled } from '../../../utils/features'

interface ShareProps {
  shareLink?: string
  groupName: string
  isAdmin: boolean
  isPayPalUser: boolean
  groupId: string
}

const Share = ({
  shareLink,
  groupName,
  isAdmin,
  isPayPalUser,
  groupId,
}: ShareProps) => {
  const { getContent } = useLocalizedContent()
  const useDetailsV1Dot1 = isDetailsV1Dot1Enabled()
  const messageAndLinkToBeShared = getContent('share.messageToShare', {
    linkToShare: shareLink,
  })
  const dispatch = useAppDispatch()

  const handleCopyClick = () => {
    dispatch(
      showGlobalToast({
        message: getContent('create.success.linkCopied'),
        isSuccess: true,
      }),
    )
    tracking(
      Events.p2p_pools_detail_share_pressed({
        source: serverData.trackingParameters.externalSource,
        web_share_button: 'copy',
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
        pool_id: groupId,
      }),
    )
    navigator.clipboard.writeText(
      addTrafficSourceParameter(shareLink, isAdmin, LINK_SOURCE.COPY),
    )
  }

  const handleFBClick = (e: MouseEvent<HTMLButtonElement>) => {
    tracking(
      Events.p2p_pools_detail_share_pressed({
        source: serverData.trackingParameters.externalSource,
        web_share_button: 'facebook',
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
        pool_id: groupId,
      }),
    )
    e.preventDefault()
    sharing.shareURLByFacebook(shareLink, 575, 400)
  }

  const handleTwitterClick = (e: MouseEvent<HTMLButtonElement>) => {
    tracking(
      Events.p2p_pools_detail_share_pressed({
        source: serverData.trackingParameters.externalSource,
        web_share_button: 'twitter',
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
        pool_id: groupId,
      }),
    )
    e.preventDefault()
    sharing.shareByTwitter(messageAndLinkToBeShared, 575, 400)
  }

  const handleMessengerClick = (e: MouseEvent<HTMLButtonElement>) => {
    tracking(
      Events.p2p_pools_detail_share_pressed({
        source: serverData.trackingParameters.externalSource,
        web_share_button: 'messenger',
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
        pool_id: groupId,
      }),
    )
    e.preventDefault()
    sharing.shareURLByMessenger(
      shareLink,
      FACEBOOK_APP_ID,
      window.location.href,
    )
  }

  const handleWhatsappClick = (e: MouseEvent<HTMLElement>) => {
    tracking(
      Events.p2p_pools_detail_share_pressed({
        source: serverData.trackingParameters.externalSource,
        web_share_button: 'whatsapp',
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
        pool_id: groupId,
      }),
    )
    e.preventDefault()
    sharing.shareByWhatsApp(messageAndLinkToBeShared)
  }

  const handleEmailClick = (e: MouseEvent<HTMLElement>) => {
    tracking(
      Events.p2p_pools_detail_share_pressed({
        source: serverData.trackingParameters.externalSource,
        web_share_button: 'email',
        page_variant: getGroupDetailPageVariant(isAdmin, isPayPalUser),
        pool_id: groupId,
      }),
    )
    e.preventDefault()
    sharing.shareByEmail(groupName, messageAndLinkToBeShared)
  }

  const shareContainerSection = (
    <Row data-testid="share-container-section">
      <Col>
        <IconButton
          size="xl"
          data-testid="copy-to-clipboard"
          buttonType="secondary"
          svgIconComponent={CopyToClipboardIcon}
          onClick={handleCopyClick}
          aria-label={getContent('share.accessibility.copyLink')}
          label={getContent('share.copyLink')}
        />
      </Col>
      <Col>
        <IconButton
          size="xl"
          data-testid="email-button"
          buttonType="secondary"
          svgIconComponent={EmailIcon}
          onClick={handleEmailClick}
          aria-label={getContent('share.accessibility.email')}
          label={getContent('share.email')}
        />
      </Col>
      <Col>
        <IconButton
          size="xl"
          data-testid="messenger-button"
          buttonType="secondary"
          svgIconComponent={ShareMessengerIcon}
          onClick={handleMessengerClick}
          aria-label={getContent('share.accessibility.messenger')}
          label={getContent('share.messenger')}
        />
      </Col>
      <Col>
        <IconButton
          size="xl"
          data-testid="facebook-button"
          buttonType="secondary"
          svgIconComponent={ShareFacebookIcon}
          onClick={handleFBClick}
          aria-label={getContent('share.accessibility.facebook')}
          label={getContent('share.facebook')}
        />
      </Col>
      <Col>
        <IconButton
          size="xl"
          data-testid="twitter-button"
          buttonType="secondary"
          svgIconComponent={ShareTwitterIcon}
          onClick={handleTwitterClick}
          aria-label={getContent('share.accessibility.twitter')}
          label={getContent('share.twitter')}
        />
      </Col>
      <Col>
        <IconButton
          size="xl"
          data-testid="whatsapp-button"
          buttonType="secondary"
          svgIconComponent={ShareWhatsappIcon}
          onClick={handleWhatsappClick}
          aria-label={getContent('share.accessibility.whatsApp')}
          label={getContent('share.whatsApp')}
        />
      </Col>
    </Row>
  )

  if (useDetailsV1Dot1) {
    return (
      <div className={styles.shareSection} data-testid="share-section">
        <TitleText
          type="strong"
          as="h2"
          className={styles.shareTitle}
          data-testid="share-title"
        >
          <Message id="share.shareGroup" />
        </TitleText>
        {shareContainerSection}
      </div>
    )
  }

  return (
    <div className={styles.shareStyles} data-testid="share-section">
      <BodyText as="p" className={styles.textAlignCenter}>
        <Message id="share.shareGroup" />
      </BodyText>
      {shareContainerSection}
    </div>
  )
}

export default Share
